import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Tag, Popconfirm, message, Card, Statistic, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { strategyApi } from '@tradermind/shared/api';
import type { Strategy } from '@tradermind/shared/types';

export default function StrategyListPage() {
  const { t } = useTranslation(['strategy', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = useQuery({
    queryKey: ['strategies', { page, limit: pageSize }],
    queryFn: () => strategyApi.getStrategies({ page, limit: pageSize }),
  });

  const deleteMutation = useMutation({
    mutationFn: strategyApi.deleteStrategy,
    onSuccess: () => {
      message.success(t('strategy:messages.deleted'));
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
    },
    onError: () => {
      message.error(t('common:error.delete_failed'));
    },
  });

  const strategies = data?.data?.items || [];
  const total = data?.data?.total || 0;

  const columns = [
    {
      title: t('strategy:strategy_name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Strategy) => (
        <a onClick={() => navigate(`/strategies/${record.id}/edit`)}>{text}</a>
      ),
    },
    {
      title: t('strategy:timeframe'),
      dataIndex: 'timeframe',
      key: 'timeframe',
      render: (timeframe: string) => timeframe || '-',
    },
    {
      title: t('strategy:conditions'),
      dataIndex: 'conditions',
      key: 'conditions',
      render: (conditions: any[]) => (
        <Tag color="blue">{conditions?.length || 0} {t('strategy:conditions').toLowerCase()}</Tag>
      ),
    },
    {
      title: t('strategy:stats.trade_count'),
      dataIndex: 'trade_count',
      key: 'trade_count',
      align: 'center' as const,
    },
    {
      title: t('strategy:stats.win_rate'),
      key: 'win_rate',
      align: 'center' as const,
      render: (_: any, record: Strategy) => {
        if (!record.trade_count) return '-';
        const winRate = (record.win_count / record.trade_count) * 100;
        return (
          <Tag color={winRate >= 50 ? 'green' : 'red'}>
            {winRate.toFixed(1)}%
          </Tag>
        );
      },
    },
    {
      title: t('strategy:stats.total_pnl'),
      dataIndex: 'total_pnl',
      key: 'total_pnl',
      align: 'right' as const,
      render: (pnl: number) => {
        if (!pnl) return '-';
        const color = pnl >= 0 ? 'green' : 'red';
        return <span style={{ color }}>${pnl.toFixed(2)}</span>;
      },
    },
    {
      title: t('common:status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? t('common:active') : t('common:inactive')}
        </Tag>
      ),
    },
    {
      title: t('common:actions'),
      key: 'actions',
      render: (_: any, record: Strategy) => (
        <Space>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/strategies/${record.id}/stats`)}
          >
            {t('strategy:stats.title')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/strategies/${record.id}/edit`)}
          >
            {t('common:edit')}
          </Button>
          <Popconfirm
            title={t('strategy:delete_confirm_title')}
            description={t('strategy:delete_confirm_message', { name: record.name })}
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText={t('common:confirm')}
            cancelText={t('common:cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 计算统计数据
  const totalTrades = strategies.reduce((sum, s) => sum + (s.trade_count || 0), 0);
  const totalWins = strategies.reduce((sum, s) => sum + (s.win_count || 0), 0);
  const totalPnl = strategies.reduce((sum, s) => sum + (s.total_pnl || 0), 0);
  const overallWinRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('strategy:title')}</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/strategies/new')}>
          {t('strategy:add_strategy')}
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('strategy:my_strategies')}
              value={strategies.length}
              suffix={t('common:items')}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('strategy:stats.trade_count')}
              value={totalTrades}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('strategy:stats.win_rate')}
              value={overallWinRate}
              precision={1}
              suffix="%"
              valueStyle={{ color: overallWinRate >= 50 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('strategy:stats.total_pnl')}
              value={totalPnl}
              precision={2}
              prefix="$"
              valueStyle={{ color: totalPnl >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={strategies}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
            showSizeChanger: true,
            showTotal: (total) => t('common:total_items', { count: total }),
          }}
        />
      </Card>
    </div>
  );
}
